import React, { useCallback, useEffect, useRef, useState } from "react";
import {Crisp} from "crisp-sdk-web"
import { useHistory, useParams, useLocation } from "react-router-dom";
import Info from '../../components/Info';
import PLANS from "../../constants/plans.json";
import PlansSkeleton from "../../components/skeletons/pages/PlansSkeleton";
import { SELECT_PLAN, VISITED_UPGRADE_PLAN } from "../../constants/hotjar-events";
import { useOrganisation } from "../../http-hooks/organisation/useOrganisation";
import { useSubscription } from "../../http-hooks/organisation/useSubscription";
import { addMixPanelEvent } from "../../store/actions";
import { useDispatch } from "react-redux";
import { returnCurrentIntegrationOfOrg } from "../../utils/General";
import { useBilling } from "../../hooks/useBilling";
import { getPlanTier } from "../../components/plans/PlanCardsGrid";

const plans = PLANS.filter(item => item.is_active).slice(1);

export default function ChangePlan() {
  const { org_id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch()

  const {isLoading, isError, data: org} = useOrganisation({org_id});

  const [isAnnualBillingCycle, setIsAnnualBillingCycle] = useState(false);

  useEffect(() => {
    window.hj("event", VISITED_UPGRADE_PLAN);
    
    // Extract source from URL params
    const urlParams = new URLSearchParams(location.search);
    const source = urlParams.get('source') || 'direct';
    
    dispatch(addMixPanelEvent({
			orgId: org_id, body: {
				eventName: "PAYMENT_VIEW_PRICING_PAGE",
				eventData: {
					source: source,
					plan_id: org?.plan_id,
					plan_tier: getPlanTier(org?.plan_id)
				}
			}
		}))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoading && !isError && org && !org.plan_id) {
      history.push(`/organisation/${org_id}/plans`);
    }
  }, [isLoading, isError, org, org_id, history]);

  if (isError) {
    return <></>
  }

  if (isLoading) {
    return <PlansSkeleton />;
  }


  if (!org.plan_id) {
    // Redirect handled by the effect above.
    return <></>
  }

  return (
    <>
      <div className="container-fluid">
        <div className="container">
          <div className="row my-5">
            <div className="col-12 mb-4">
              <h2 className="text-center fw-bold mb-2">Upgrade Your Plan</h2>
              <p className="text-center text-muted mb-4">Choose a plan that fits your growing needs</p>
            </div>
            <div className="col-12">
              <div className="d-flex align-items-center justify-content-center gap-3">
                <span className={`fw-semibold ${!isAnnualBillingCycle ? 'text-primary' : 'text-muted'}`}>
                  Monthly
                </span>
                <div className="form-check form-switch mb-0">
                  <input 
                    onChange={(e) => setIsAnnualBillingCycle(e.target.checked)} 
                    type="checkbox" 
                    className="form-check-input" 
                    style={{ cursor: 'pointer', width: '3rem', height: '1.5rem' }}
                    id="billing_cycle_toggle" 
                    name="billing_cycle_toggle"
                    checked={isAnnualBillingCycle}
                  />
                </div>
                <span className={`fw-semibold ${isAnnualBillingCycle ? 'text-primary' : 'text-muted'}`}>
                  Annual
                  <span className="badge bg-success ms-2 align-middle" style={{ fontSize: '0.7rem' }}>
                    Save 1 month
                  </span>
                </span>
              </div>
            </div>
          </div>
          <PlansList org={org} isAnnualBillingCycle={isAnnualBillingCycle} />
        </div>
      </div>
    </>
  );
}

const PlansList = ({ org, isAnnualBillingCycle }) => {

  const { org_id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const { startCheckout, createUpgradeHostedPageFn } = useBilling({ orgId: org_id });
  const { search } = useLocation();
	const queryString = new URLSearchParams(search)
	const plan_id = queryString.get("plan_id")
  const {isLoading, data: subscription} = useSubscription({ org_id });
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false)
  const hasAutoSelectedRef = useRef(false)

  const openUpgradeCard = useCallback(async (planId) => {

    try {
      const integrationId = await returnCurrentIntegrationOfOrg(org_id)
      const successURL = window.location.origin + `/organisation/${org_id}/integration/${integrationId}/billing`;
      // const successURL = `${}`
      console.log({successURL, integrationId})
      const failureURL = window.location.href
      
      // Get source from URL params if user came from a specific feature
      const urlParams = new URLSearchParams(window.location.search);
      const source = urlParams.get('source') || 'upgrade_page';

      const hostedPageFn = createUpgradeHostedPageFn({
        planId,
        redirectURL: successURL,
        cancelURL: failureURL,
        source,
      });

      await startCheckout({
        hostedPageFn,
        afterValidate: async () => {
          history.push(`/organisation/${org_id}/integration/${integrationId}/billing`);
        },
        onClose: function () {
          // user closed popup; keep them on upgrade page
        },
        layout: "full_page",
        planId,
      });

    } catch (e) {
      // errors already toasted + notified in useBilling
    }
  }, [org_id, history, startCheckout, createUpgradeHostedPageFn])

  const onSelectPlan = useCallback((plan, subscription, source = "change_plan_page") => {
    window.hj("event", SELECT_PLAN);

    const selectedPlanId = subscription.plan_id.indexOf('usd') !== -1
      ? (isAnnualBillingCycle ? plan.plan_id_usd_annual : plan.plan_id_usd)
      : (isAnnualBillingCycle ? plan.plan_id_inr_annual : plan.plan_id_inr);

    dispatch(addMixPanelEvent({
      orgId: org_id, body: {
        eventName: "SELECT_PLAN",
        eventData: {
          plan_id: selectedPlanId,
          plan_tier: getPlanTier(selectedPlanId),
          source,
        }
      }
    }));

    openUpgradeCard(selectedPlanId);
  }, [isAnnualBillingCycle, openUpgradeCard, dispatch, org_id])

  useEffect(() => {
    if(plan_id && !isCheckoutLoading){
      setIsCheckoutLoading(true)
    }

    if(plan_id && subscription){
      const planQuery = plans.find(p => {
        return (plan_id === p.plan_id_inr ||
          plan_id === p.plan_id_usd ||
          plan_id === p.plan_id_inr_annual ||
          plan_id === p.plan_id_usd_annual)
      })
      if(planQuery && !hasAutoSelectedRef.current){
        hasAutoSelectedRef.current = true
        onSelectPlan(planQuery, subscription, "deep_link")
      }else if(!planQuery){
        setIsCheckoutLoading(false)
      }
    }
  }, [subscription, plan_id, isCheckoutLoading, onSelectPlan])

  useEffect(() => {
    if (!isLoading && (!subscription || !subscription.plan_id)) {
      history.push(`/organisation/${org_id}/plans`);
    }
  }, [isLoading, subscription, org_id, history]);

  if (isLoading || isCheckoutLoading) {
    return <PlansSkeleton showToggle={false} />;
  }

  let currentPlan;

  if (!subscription || !subscription.plan_id) {
    // Redirect handled by the effect above.
    return <></>
  }

  if (org.subscription_status !== 'cancelled') {
    currentPlan = plans.filter((plan) => {
      return (
        plan.plan_id_usd === org.plan_id ||
        plan.plan_id_inr === org.plan_id ||
        plan.plan_id_usd_annual === org.plan_id ||
        plan.plan_id_inr_annual === org.plan_id
      );
    })[0];
  }

  return (
    <>
      <div className="row mt-5 justify-content-center g-4">
        {plans.map((plan) => {
          return (
            <div className="col-12 col-md-6 col-lg-4 col-xl-3" key={plan.plan_id_inr}>
              <div className="card shadow-sm border h-100">
                <div className="card-body d-flex flex-column">
                  <h4 className="card-title text-center fw-bold mb-1">
                    {plan.plan_title}
                  </h4>
                  <p className="text-center text-muted small mb-3">
                    {plan.plan_sub_title}
                  </p>
                  <div className="text-center mb-3">
                    <h2 className="fw-bold mb-2">{isAnnualBillingCycle ? `$${plan.annual_plan_monthly_price}/month` : `${plan.price}/month`}</h2>
                  </div>
                  {isAnnualBillingCycle ? (
                    <div className="text-center text-muted small mb-3">
                      <span>${plan.annual_price} Billed Annually</span>
                    </div>
                  ) : (
                    <div className="text-center text-muted small mb-3">
                      <span>Billed Monthly</span>
                    </div>
                  )}
                  
                  {(subscription && subscription.status !== 'cancelled') && (org.plan_id === plan.plan_id_inr ||
                  org.plan_id === plan.plan_id_usd ||
                  org.plan_id === plan.plan_id_inr_annual ||
                  org.plan_id === plan.plan_id_usd_annual) ? (
                    <div className="d-grid gap-2 mb-3">
                      <button
                        type="button"
                        disabled
                        className="btn btn-primary btn-lg"
                      >
                        Current Plan
                      </button>
                    </div>
                  ) : (
                    <div className="d-grid gap-2 mb-3">
                      <button
                        onClick={() => onSelectPlan(plan, subscription)}
                        className="btn btn-outline-primary btn-lg"
                      >
                        {currentPlan
                          ? (((plan.price > currentPlan.price) || (isAnnualBillingCycle && !org?.plan_id?.includes("annual")))
                            ? "Upgrade"
                            : "Downgrade")
                          : "Choose Plan"}
                      </button>
                    </div>
                  )}
                  
                  <div className="text-center mb-3 py-2 bg-light rounded">
                    <strong>{plan.words_count.toLocaleString()}</strong> New Words / Month
                  </div>
                  
                  <hr className="my-3" />
                  <p className="fw-bold mb-3 text-center">Key Features</p>
                  <ul className="list-unstyled mt-auto">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={`${plan.plan_id_inr}-feature-${featureIndex}`} className="mb-2">
                        <span className="text-primary me-2">✓</span>
                        {feature.text} {feature.help_text ? <Info text={feature.help_text} /> : null}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="row my-5 align-items-center justify-content-center">
        <div className="col-md-9 text-center">
          <span style={{ fontSize: "24px", color: "var(--bs-body-color)" }}>
            Do not fit into any of the plans? Chat with us
            <br /> and get an{" "}
            <span style={{ color: "var(--bs-primary)" }}>Enterprise Plan</span>
          </span>
        </div>
        <div className="col-md-3">
          <button
            type="button"
            onClick={(e) => {
              Crisp.chat.open()
            }}
            className="btn btn-primary w-100 mt-3 mt-sm-0"
          >
            Talk to us
          </button>
        </div>
      </div>
    </>
  )
}
